import { useState, useEffect, useRef, useCallback } from 'react';
import { Midi } from '@tonejs/midi';
import * as Tone from 'tone';

/**
 * Custom hook for MIDI file playback with Tone.js
 * Provides tempo-aware playback and real-time position tracking
 */
export const useMidiPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [midiData, setMidiData] = useState(null);
  const [expectedNotes, setExpectedNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const synthRef = useRef(null);
  const partRef = useRef(null);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(0);

  // Initialize synth
  useEffect(() => {
    synthRef.current = new Tone.PolySynth(Tone.Synth, {
      volume: -8,
      oscillator: { type: 'sine' },
      envelope: {
        attack: 0.01,
        decay: 0.1,
        sustain: 0.3,
        release: 0.5
      }
    })?.toDestination();

    return () => {
      if (synthRef?.current) {
        synthRef?.current?.dispose();
      }
    };
  }, []);

  // Load MIDI file
  const loadMidiFile = useCallback(async (file, selectedTrackIndex = 0) => {
    setIsLoading(true);
    setError(null);

    try {
      // Read file as array buffer
      const arrayBuffer = await file?.arrayBuffer();
      const midi = new Midi(arrayBuffer);

      if (!midi?.tracks || midi?.tracks?.length === 0) {
        throw new Error('No tracks found in MIDI file');
      }

      // Get selected track
      const track = midi?.tracks?.[selectedTrackIndex];
      
      // Convert MIDI notes to expected notes timeline
      const timeline = track?.notes?.map(note => ({
        time: note?.time,
        duration: note?.duration,
        midi: note?.midi,
        name: note?.name,
        frequency: Tone?.Frequency(note?.midi, 'midi')?.toFrequency(),
        velocity: note?.velocity
      }))?.sort((a, b) => a?.time - b?.time);

      setExpectedNotes(timeline);
      setDuration(track?.duration);
      setMidiData({ midi, track, timeline });

      // Create Tone.js Part for playback
      if (partRef?.current) {
        partRef?.current?.dispose();
      }

      const part = new Tone.Part((time, note) => {
        if (synthRef.current) {
          synthRef.current.triggerAttackRelease(
            Tone.Frequency(note.midi, 'midi'),
            note.duration,
            time,
            note.velocity
          );
        }
      }, track.notes.map(n => ({
        time: n.time,
        midi: n.midi,
        duration: n.duration,
        velocity: n.velocity
      })));

      part.loop = false;
      partRef.current = part;

      setIsLoading(false);
      return { success: true, trackInfo: { duration: track?.duration, noteCount: track?.notes?.length } };
    } catch (err) {
      console.error('Error loading MIDI file:', err);
      setError('Failed to load MIDI file. Please ensure it\'s a valid MIDI file.');
      setIsLoading(false);
      return { success: false, error: err?.message };
    }
  }, []);

  // Update current time during playback
  const updateTime = useCallback(() => {
    if (isPlaying && startTimeRef?.current > 0) {
      const elapsed = Tone?.Transport?.seconds - startTimeRef?.current;
      setCurrentTime(elapsed);
      
      if (elapsed >= duration) {
        stop();
      } else {
        animationFrameRef.current = requestAnimationFrame(updateTime);
      }
    }
  }, [isPlaying, duration]);

  // Start playback
  const play = useCallback(async () => {
    if (!partRef?.current || isPlaying) return;

    try {
      await Tone?.start();
      
      if (Tone?.Transport?.state !== 'started') {
        startTimeRef.current = Tone?.Transport?.seconds;
        Tone?.Transport?.start();
      }

      partRef?.current?.start(0);
      setIsPlaying(true);
      updateTime();
    } catch (err) {
      console.error('Error starting playback:', err);
      setError('Failed to start playback');
    }
  }, [isPlaying, updateTime]);

  // Pause playback
  const pause = useCallback(() => {
    if (!isPlaying) return;

    Tone?.Transport?.pause();
    if (partRef?.current) {
      partRef?.current?.stop();
    }
    
    if (animationFrameRef?.current) {
      cancelAnimationFrame(animationFrameRef?.current);
    }
    
    setIsPlaying(false);
  }, [isPlaying]);

  // Stop playback
  const stop = useCallback(() => {
    pause();
    Tone?.Transport?.stop();
    setCurrentTime(0);
    startTimeRef.current = 0;
  }, [pause]);

  // Seek to position
  const seek = useCallback((time) => {
    const wasPlaying = isPlaying;
    if (wasPlaying) pause();
    
    setCurrentTime(time);
    startTimeRef.current = Tone?.Transport?.seconds - time;
    
    if (wasPlaying) play();
  }, [isPlaying, pause, play]);

  // Get expected note at current time
  const getExpectedNoteAt = useCallback((time, timingWindow = 0.12) => {
    if (!expectedNotes || expectedNotes?.length === 0) return null;

    // Find notes within timing window
    const matchingNotes = expectedNotes?.filter(note => {
      const noteStart = note?.time;
      const noteEnd = note?.time + note?.duration;
      return time >= noteStart - timingWindow && time <= noteEnd + timingWindow;
    });

    // Return the closest note
    if (matchingNotes?.length === 0) return null;
    
    return matchingNotes?.reduce((closest, note) => {
      const currentDiff = Math.abs(time - note?.time);
      const closestDiff = Math.abs(time - closest?.time);
      return currentDiff < closestDiff ? note : closest;
    });
  }, [expectedNotes]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrameRef?.current) {
        cancelAnimationFrame(animationFrameRef?.current);
      }
      if (partRef?.current) {
        partRef?.current?.dispose();
      }
    };
  }, []);

  return {
    loadMidiFile,
    play,
    pause,
    stop,
    seek,
    getExpectedNoteAt,
    isPlaying,
    isLoading,
    currentTime,
    duration,
    expectedNotes,
    midiData,
    error
  };
};